// TeacherSmartAttendanceDashboard.jsx

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";



import {
    Alert,
    Badge,
    Card,
    Col,
    Divider,
    Empty,
    Progress,
    Row,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Typography,
} from "antd";

import {
    CheckCircleFilled,
    ClockCircleFilled,
    CloseCircleFilled,
    DashboardOutlined,
    LoginOutlined,
    LogoutOutlined,
    ReloadOutlined,
    ScanOutlined,
    SafetyCertificateFilled,
    SignalFilled,
    TeamOutlined,
    WarningFilled,
    WifiOutlined, 
} from "@ant-design/icons";

import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";

const {
    Title,
    Text,
} = Typography;


// =========================================================
// CONFIGURATION
// =========================================================

const SCAN_LOG_ENDPOINT =
    "/api/smart-teacher-attendance/scan-logs/";


// Change this if your Django Channels route
// uses another websocket path.
const WEBSOCKET_PATH =
    "/ws/smart-teacher-attendance/";


// Polling is only a fallback when WebSocket
// is unavailable.
const POLLING_INTERVAL =
    10000;


// Number of records shown in the live table.
const MAX_VISIBLE_LOGS =
    100;


// =========================================================
// ACTION CONSTANTS
// =========================================================

const ACTION_CHECK_IN =
    "check_in";

const ACTION_CHECK_OUT =
    "check_out";

const ACTION_REJECTED =
    "rejected";

const ACTION_DUPLICATE =
    "duplicate";


// =========================================================
// COLORS
// =========================================================

const COLORS = {

    orange:
        "#f97316",

    orangeDark:
        "#ea580c",

    black:
        "#111827",

    blackSoft:
        "#1f2937",

    gray:
        "#6b7280",

    grayLight:
        "#f3f4f6",

    grayBorder:
        "#e5e7eb",

    white:
        "#ffffff",

    green:
        "#16a34a",

    red:
        "#dc2626",

    yellow:
        "#d97706",

    blue:
        "#2563eb",

};


// =========================================================
// HELPER FUNCTIONS
// =========================================================

const getWebSocketProtocol = () => {

    if (
        window.location.protocol ===
        "https:"
    ) {

        return "wss:";

    }

    return "ws:";

};


const getWebSocketUrl = () => {

    const protocol =
        getWebSocketProtocol();

    const host =
        window.location.host;

    return (
        `${protocol}//${host}${WEBSOCKET_PATH}`
    );

};


const getActionLabel = (
    action
) => {

    switch (action) {

        case ACTION_CHECK_IN:
            return "Check In";

        case ACTION_CHECK_OUT:
            return "Check Out";

        case ACTION_REJECTED:
            return "Rejected";

        case ACTION_DUPLICATE:
            return "Duplicate";

        default:
            return action || "Unknown";

    }

};


const getActionColor = (
    action
) => {

    switch (action) {

        case ACTION_CHECK_IN:
            return "green";

        case ACTION_CHECK_OUT:
            return "blue";

        case ACTION_REJECTED:
            return "red";

        case ACTION_DUPLICATE:
            return "orange";

        default:
            return "default";

    }

};


const getActionIcon = (
    action
) => {

    switch (action) {

        case ACTION_CHECK_IN:
            return (
                <LoginOutlined />
            );

        case ACTION_CHECK_OUT:
            return (
                <LogoutOutlined />
            );

        case ACTION_REJECTED:
            return (
                <CloseCircleFilled />
            );

        case ACTION_DUPLICATE:
            return (
                <WarningFilled />
            );

        default:
            return (
                <ScanOutlined />
            );

    }

};


const getTeacherName = (
    log
) => {

    return (
        log?.teacher?.full_name ||
        log?.teacher?.name ||
        log?.teacher_name ||
        "Unknown Teacher"
    );

};


const getDeviceName = (
    log
) => {

    return (
        log?.device?.device_name ||
        log?.device?.name ||
        log?.device_name ||
        "Unknown Device"
    );

};


const getGateName = (
    log
) => {

    return (
        log?.device?.gate_name ||
        log?.gate_name ||
        log?.gate ||
        "--"
    );

};


const getCardNumber = (
    log
) => {

    return (
        log?.card?.card_number ||
        log?.card?.card_uid ||
        log?.card_number ||
        log?.qr_token ||
        "--"
    );

};


const getTimestamp = (
    log
) => {

    return (
        log?.scanned_at ||
        log?.timestamp ||
        log?.created_at ||
        null
    );

};


const formatDateTime = (
    value
) => {

    if (!value) {
        return "--";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return "--";

    }

    return date.toLocaleString(
        [],
        {
            year:
                "numeric",

            month:
                "short",

            day:
                "2-digit",

            hour:
                "2-digit",

            minute:
                "2-digit",

            second:
                "2-digit",
        }
    );

};


const formatTime = (
    value
) => {

    if (!value) {
        return "--";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return "--";

    }

    return date.toLocaleTimeString(
        [],
        {
            hour:
                "2-digit",

            minute:
                "2-digit",

            second:
                "2-digit",
        }
    );

};


const getTodayKey = () => {

    const now =
        new Date();

    return (
        `${now.getFullYear()}-` +
        `${String(
            now.getMonth() + 1
        ).padStart(2, "0")}-` +
        `${String(
            now.getDate()
        ).padStart(2, "0")}`
    );

};


const getDateKey = (
    value
) => {

    if (!value) {
        return null;
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return null;

    }

    return (
        `${date.getFullYear()}-` +
        `${String(
            date.getMonth() + 1
        ).padStart(2, "0")}-` +
        `${String(
            date.getDate()
        ).padStart(2, "0")}`
    );

};


// =========================================================
// NORMALIZE API DATA
// =========================================================

const normalizeLogs = (
    responseData
) => {

    if (
        Array.isArray(
            responseData
        )
    ) {

        return responseData;

    }

    if (
        Array.isArray(
            responseData?.results
        )
    ) {

        return responseData.results;

    }

    if (
        Array.isArray(
            responseData?.data
        )
    ) {

        return responseData.data;

    }

    if (
        Array.isArray(
            responseData?.logs
        )
    ) {

        return responseData.logs;

    }

    return [];

};


// =========================================================
// COMPONENT
// =========================================================

const TeacherSmartAttendanceDashboard =
    () => {

        // =================================================
        // ACADEMIC YEAR
        // =================================================

        const {
            selectedYear,
        } = useAcademicYear();


        // =================================================
        // STATE
        // =================================================

        const [logs, setLogs] =
            useState([]);

        const [loading, setLoading] =
            useState(true);

        const [refreshing, setRefreshing] =
            useState(false);

        const [error, setError] =
            useState(null);

        const [lastUpdated, setLastUpdated] =
            useState(null);

        const [connectionStatus, setConnectionStatus] =
            useState("connecting");

        const [wsConnected, setWsConnected] =
            useState(false);

        const [liveEvent, setLiveEvent] =
            useState(null);


        // =================================================
        // REFS
        // =================================================

        const socketRef =
            useRef(null);

        const reconnectTimerRef =
            useRef(null);

        const pollingTimerRef =
            useRef(null);

        const reconnectAttemptRef =
            useRef(0);


        // =================================================
        // FETCH LOGS
        // =================================================

        const fetchLogs = useCallback(
            async (
                showLoader = false
            ) => {

                if (!selectedYear?.id) {
                    return;
                }


                try {

                    if (showLoader) {

                        setLoading(
                            true
                        );

                    } else {

                        setRefreshing(
                            true
                        );

                    }


                    const response =
                        await api.get(
                            SCAN_LOG_ENDPOINT,
                            {
                                params: {

                                    academic_year:
                                        selectedYear.id,

                                    page_size:
                                        MAX_VISIBLE_LOGS,

                                },

                            }
                        );


                    const incomingLogs =
                        normalizeLogs(
                            response.data
                        );


                    setLogs(
                        incomingLogs
                            .slice(
                                0,
                                MAX_VISIBLE_LOGS
                            )
                    );


                    setLastUpdated(
                        new Date()
                    );


                    setError(
                        null
                    );

                } catch (fetchError) {

                    console.error(
                        "Failed to load attendance logs:",
                        fetchError
                    );


                    const message =
                        fetchError
                            ?.response
                            ?.data
                            ?.detail ||
                        fetchError
                            ?.response
                            ?.data
                            ?.message ||
                        "Unable to load teacher attendance logs.";


                    setError(
                        message
                    );

                } finally {

                    setLoading(
                        false
                    );

                    setRefreshing(
                        false
                    );

                }

            },
            [
                selectedYear?.id,
            ]
        );


        // =================================================
        // INSERT REAL-TIME LOG
        // =================================================

        const insertRealtimeLog =
            useCallback(
                (newLog) => {

                    if (!newLog) {
                        return;
                    }


                    // Ignore another academic year.
                    if (
                        newLog.academic_year &&
                        selectedYear?.id &&
                        Number(
                            newLog.academic_year
                        ) !==
                            Number(
                                selectedYear.id
                            )
                    ) {

                        return;

                    }


                    setLogs(
                        previousLogs => {

                            // Prevent duplicate websocket
                            // events.

                            const incomingId =
                                newLog.id;

                            if (
                                incomingId &&
                                previousLogs.some(
                                    log =>
                                        log.id ===
                                        incomingId
                                )
                            ) {

                                return previousLogs;

                            }


                            const updated = [
                                newLog,
                                ...previousLogs,
                            ];


                            return updated.slice(
                                0,
                                MAX_VISIBLE_LOGS
                            );

                        }
                    );


                    setLastUpdated(
                        new Date()
                    );


                    setLiveEvent(
                        newLog
                    );


                    setTimeout(
                        () => {

                            setLiveEvent(
                                null
                            );

                        },
                        5000
                    );

                },
                [
                    selectedYear?.id,
                ]
            );


        // =================================================
        // WEBSOCKET
        // =================================================

        const connectWebSocket =
            useCallback(
                () => {

                    if (
                        !selectedYear?.id
                    ) {

                        return;

                    }


                    try {

                        if (
                            socketRef.current
                        ) {

                            try {

                                socketRef.current.close();

                            } catch (
                                closeError
                            ) {

                                // Ignore.

                            }

                        }


                        setConnectionStatus(
                            "connecting"
                        );


                        const socket =
                            new WebSocket(
                                getWebSocketUrl()
                            );


                        socketRef.current =
                            socket;


                        socket.onopen =
                            () => {

                                reconnectAttemptRef.current =
                                    0;

                                setWsConnected(
                                    true
                                );

                                setConnectionStatus(
                                    "connected"
                                );


                                // Send academic year
                                // to the consumer.

                                try {

                                    socket.send(
                                        JSON.stringify(
                                            {
                                                type:
                                                    "subscribe",

                                                academic_year:
                                                    selectedYear.id,
                                            }
                                        )
                                    );

                                } catch (
                                    sendError
                                ) {

                                    console.error(
                                        "WebSocket subscribe error:",
                                        sendError
                                    );

                                }

                            };


                        socket.onmessage =
                            event => {

                                try {

                                    const message =
                                        JSON.parse(
                                            event.data
                                        );


                                    // Supported formats:
                                    //
                                    // {
                                    //   type: "scan_log",
                                    //   data: {...}
                                    // }
                                    //
                                    // or:
                                    //
                                    // {
                                    //   type: "attendance_scan",
                                    //   log: {...}
                                    // }
                                    //
                                    // or direct log object.

                                    const payload =
                                        message?.data ||
                                        message?.log ||
                                        (
                                            message?.type ===
                                            "scan_log"
                                                ? message
                                                : null
                                        );


                                    if (
                                        payload
                                    ) {

                                        insertRealtimeLog(
                                            payload
                                        );

                                    }

                                } catch (
                                    messageError
                                ) {

                                    console.error(
                                        "Invalid WebSocket message:",
                                        messageError
                                    );

                                }

                            };


                        socket.onerror =
                            socketError => {

                                console.error(
                                    "Attendance WebSocket error:",
                                    socketError
                                );

                                setWsConnected(
                                    false
                                );

                                setConnectionStatus(
                                    "fallback"
                                );

                            };


                        socket.onclose =
                            () => {

                                setWsConnected(
                                    false
                                );

                                setConnectionStatus(
                                    "fallback"
                                );


                                const attempt =
                                    reconnectAttemptRef.current;


                                const delay =
                                    Math.min(
                                        30000,
                                        2000 *
                                        Math.pow(
                                            2,
                                            attempt
                                        )
                                    );


                                reconnectAttemptRef.current =
                                    attempt + 1;


                                if (
                                    reconnectTimerRef.current
                                ) {

                                    clearTimeout(
                                        reconnectTimerRef.current
                                    );

                                }


                                reconnectTimerRef.current =
                                    setTimeout(
                                        () => {

                                            connectWebSocket();

                                        },
                                        delay
                                    );

                            };

                    } catch (
                        websocketError
                    ) {

                        console.error(
                            "Unable to create WebSocket:",
                            websocketError
                        );

                        setWsConnected(
                            false
                        );

                        setConnectionStatus(
                            "fallback"
                        );

                    }

                },
                [
                    selectedYear?.id,
                    insertRealtimeLog,
                ]
            );


        // =================================================
        // INITIAL DATA
        // =================================================

        useEffect(
            () => {

                if (
                    !selectedYear?.id
                ) {

                    return;

                }


                fetchLogs(
                    true
                );

            },
            [
                selectedYear?.id,
                fetchLogs,
            ]
        );


        // =================================================
        // START WEBSOCKET
        // =================================================

        useEffect(
            () => {

                if (
                    !selectedYear?.id
                ) {

                    return;

                }


                connectWebSocket();


                return () => {

                    if (
                        reconnectTimerRef.current
                    ) {

                        clearTimeout(
                            reconnectTimerRef.current
                        );

                    }


                    if (
                        socketRef.current
                    ) {

                        try {

                            socketRef.current.close();

                        } catch (
                            closeError
                        ) {

                            // Ignore.

                        }

                    }

                    socketRef.current =
                        null;

                    setWsConnected(
                        false
                    );

                };

            },
            [
                selectedYear?.id,
                connectWebSocket,
            ]
        );


        // =================================================
        // POLLING FALLBACK
        // =================================================

        useEffect(
            () => {

                if (
                    !selectedYear?.id
                ) {

                    return;

                }


                pollingTimerRef.current =
                    setInterval(
                        () => {

                            // If WebSocket is alive,
                            // avoid unnecessary requests.

                            if (
                                !wsConnected
                            ) {

                                fetchLogs(
                                    false
                                );

                            }

                        },
                        POLLING_INTERVAL
                    );


                return () => {

                    if (
                        pollingTimerRef.current
                    ) {

                        clearInterval(
                            pollingTimerRef.current
                        );

                    }

                };

            },
            [
                selectedYear?.id,
                wsConnected,
                fetchLogs,
            ]
        );


        // =================================================
        // CALCULATED STATISTICS
        // =================================================

        const statistics =
            useMemo(
                () => {

                    const todayKey =
                        getTodayKey();


                    const todayLogs =
                        logs.filter(
                            log =>
                                getDateKey(
                                    getTimestamp(
                                        log
                                    )
                                ) ===
                                todayKey
                        );


                    const checkIns =
                        todayLogs.filter(
                            log =>
                                log.action ===
                                ACTION_CHECK_IN &&
                                (
                                    log.success ===
                                    true ||
                                    log.success ===
                                    undefined
                                )
                        );


                    const checkOuts =
                        todayLogs.filter(
                            log =>
                                log.action ===
                                ACTION_CHECK_OUT &&
                                (
                                    log.success ===
                                    true ||
                                    log.success ===
                                    undefined
                                )
                        );


                    const rejected =
                        todayLogs.filter(
                            log =>
                                log.action ===
                                ACTION_REJECTED
                        );


                    const duplicates =
                        todayLogs.filter(
                            log =>
                                log.action ===
                                ACTION_DUPLICATE
                        );


                    const successful =
                        todayLogs.filter(
                            log =>
                                log.success ===
                                true
                        );


                    const total =
                        todayLogs.length;


                    const successRate =
                        total > 0
                            ? Math.round(
                                (
                                    successful.length /
                                    total
                                ) *
                                100
                            )
                            : 0;


                    const uniqueTeachers =
                        new Set(
                            todayLogs
                                .filter(
                                    log =>
                                        log.teacher
                                )
                                .map(
                                    log =>
                                        log.teacher.id ||
                                        getTeacherName(
                                            log
                                        )
                                )
                        );


                    return {

                        todayLogs,

                        checkIns,

                        checkOuts,

                        rejected,

                        duplicates,

                        successful,

                        total,

                        successRate,

                        uniqueTeachers:
                            uniqueTeachers.size,

                    };

                },
                [
                    logs,
                ]
            );


        // =================================================
        // HOURLY CHART
        // =================================================

        const hourlyData =
            useMemo(
                () => {

                    const hours =
                        Array.from(
                            {
                                length:
                                    24,
                            },
                            (
                                _,
                                hour
                            ) => {

                                return {

                                    hour,

                                    label:
                                        `${String(
                                            hour
                                        ).padStart(
                                            2,
                                            "0"
                                        )}:00`,

                                    checkIn:
                                        0,

                                    checkOut:
                                        0,

                                    rejected:
                                        0,

                                    duplicate:
                                        0,

                                };

                            }
                        );


                    statistics.todayLogs.forEach(
                        log => {

                            const timestamp =
                                getTimestamp(
                                    log
                                );

                            if (!timestamp) {
                                return;
                            }


                            const date =
                                new Date(
                                    timestamp
                                );


                            const hour =
                                date.getHours();


                            if (
                                log.action ===
                                ACTION_CHECK_IN
                            ) {

                                hours[
                                    hour
                                ].checkIn += 1;

                            } else if (
                                log.action ===
                                ACTION_CHECK_OUT
                            ) {

                                hours[
                                    hour
                                ].checkOut += 1;

                            } else if (
                                log.action ===
                                ACTION_REJECTED
                            ) {

                                hours[
                                    hour
                                ].rejected += 1;

                            } else if (
                                log.action ===
                                ACTION_DUPLICATE
                            ) {

                                hours[
                                    hour
                                ].duplicate += 1;

                            }

                        }
                    );


                    return hours;

                },
                [
                    statistics.todayLogs,
                ]
            );


        // =================================================
        // ACTION DISTRIBUTION
        // =================================================

        const actionChartData =
            useMemo(
                () => {

                    return [

                        {
                            name:
                                "Check In",

                            value:
                                statistics.checkIns
                                    .length,

                            color:
                                COLORS.green,
                        },

                        {
                            name:
                                "Check Out",

                            value:
                                statistics.checkOuts
                                    .length,

                            color:
                                COLORS.blue,
                        },

                        {
                            name:
                                "Rejected",

                            value:
                                statistics.rejected
                                    .length,

                            color:
                                COLORS.red,
                        },

                        {
                            name:
                                "Duplicate",

                            value:
                                statistics.duplicates
                                    .length,

                            color:
                                COLORS.orange,
                        },

                    ];

                },
                [
                    statistics,
                ]
            );


        // =================================================
        // DEVICE/GATE DATA
        // =================================================

        const deviceData =
            useMemo(
                () => {

                    const map =
                        new Map();


                    statistics.todayLogs.forEach(
                        log => {

                            const device =
                                getDeviceName(
                                    log
                                );


                            if (
                                !map.has(
                                    device
                                )
                            ) {

                                map.set(
                                    device,
                                    {
                                        device,
                                        scans:
                                            0,

                                        successful:
                                            0,

                                        latest:
                                            null,

                                        gate:
                                            getGateName(
                                                log
                                            ),
                                    }
                                );

                            }


                            const item =
                                map.get(
                                    device
                                );


                            item.scans += 1;


                            if (
                                log.success ===
                                true
                            ) {

                                item.successful += 1;

                            }


                            if (
                                !item.latest ||
                                new Date(
                                    getTimestamp(
                                        log
                                    )
                                ) >
                                    new Date(
                                        item.latest
                                    )
                            ) {

                                item.latest =
                                    getTimestamp(
                                        log
                                    );

                            }

                        }
                    );


                    return Array.from(
                        map.values()
                    );

                },
                [
                    statistics.todayLogs,
                ]
            );


        // =================================================
        // ACTIVE TEACHERS
        // =================================================

        const activeTeachers =
            useMemo(
                () => {

                    const teacherMap =
                        new Map();


                    statistics.checkIns
                        .forEach(
                            log => {

                                const teacherId =
                                    log?.teacher?.id ||
                                    getTeacherName(
                                        log
                                    );


                                teacherMap.set(
                                    teacherId,
                                    {
                                        id:
                                            teacherId,

                                        name:
                                            getTeacherName(
                                                log
                                            ),

                                        lastAction:
                                            ACTION_CHECK_IN,

                                        lastTime:
                                            getTimestamp(
                                                log
                                            ),

                                        log,

                                    }
                                );

                            }
                        );


                    statistics.checkOuts
                        .forEach(
                            log => {

                                const teacherId =
                                    log?.teacher?.id ||
                                    getTeacherName(
                                        log
                                    );


                                teacherMap.set(
                                    teacherId,
                                    {
                                        id:
                                            teacherId,

                                        name:
                                            getTeacherName(
                                                log
                                            ),

                                        lastAction:
                                            ACTION_CHECK_OUT,

                                        lastTime:
                                            getTimestamp(
                                                log
                                            ),

                                        log,

                                    }
                                );

                            }
                        );


                    return Array.from(
                        teacherMap.values()
                    )
                        .filter(
                            teacher =>
                                teacher.lastAction ===
                                ACTION_CHECK_IN
                        )
                        .sort(
                            (
                                a,
                                b
                            ) =>
                                new Date(
                                    b.lastTime
                                ) -
                                new Date(
                                    a.lastTime
                                )
                        );

                },
                [
                    statistics.checkIns,
                    statistics.checkOuts,
                ]
            );


        // =================================================
        // TABLE COLUMNS
        // =================================================

        const columns =
            useMemo(
                () => [

                    {
                        title:
                            "TIME",

                        dataIndex:
                            "scanned_at",

                        key:
                            "time",

                        width:
                            115,

                        render:
                            (_, record) => (

                                <Text
                                    strong
                                >
                                    {
                                        formatTime(
                                            getTimestamp(
                                                record
                                            )
                                        )
                                    }
                                </Text>

                            ),
                    },


                    {
                        title:
                            "TEACHER",

                        key:
                            "teacher",

                        render:
                            (_, record) => (

                                <Space>

                                    <div
                                        className="teacher-avatar"
                                    >
                                        {
                                            getTeacherName(
                                                record
                                            )
                                                .charAt(
                                                    0
                                                )
                                                .toUpperCase()
                                        }
                                    </div>

                                    <div>

                                        <Text
                                            strong
                                        >
                                            {
                                                getTeacherName(
                                                    record
                                                )
                                            }
                                        </Text>

                                        <div>

                                            <Text
                                                type="secondary"
                                                style={{
                                                    fontSize:
                                                        12,
                                                }}
                                            >
                                                Card:
                                                {" "}
                                                {
                                                    getCardNumber(
                                                        record
                                                    )
                                                }
                                            </Text>

                                        </div>

                                    </div>

                                </Space>

                            ),
                    },


                    {
                        title:
                            "ACTION",

                        key:
                            "action",

                        width:
                            145,

                        render:
                            (_, record) => (

                                <Tag
                                    icon={
                                        getActionIcon(
                                            record.action
                                        )
                                    }
                                    color={
                                        getActionColor(
                                            record.action
                                        )
                                    }
                                >
                                    {
                                        getActionLabel(
                                            record.action
                                        )
                                    }
                                </Tag>

                            ),
                    },


                    {
                        title:
                            "GATE",

                        key:
                            "gate",

                        responsive:
                            [
                                "md",
                            ],

                        render:
                            (_, record) => (

                                <Text>
                                    {
                                        getGateName(
                                            record
                                        )
                                    }
                                </Text>

                            ),
                    },


                    {
                        title:
                            "DEVICE",

                        key:
                            "device",

                        responsive:
                            [
                                "lg",
                            ],

                        render:
                            (_, record) => (

                                <Text>
                                    {
                                        getDeviceName(
                                            record
                                        )
                                    }
                                </Text>

                            ),
                    },


                    {
                        title:
                            "STATUS",

                        key:
                            "status",

                        width:
                            110,

                        render:
                            (_, record) => (

                                record.success
                                    ? (
                                        <Badge
                                            status="success"
                                            text="Success"
                                        />
                                    )
                                    : (
                                        <Badge
                                            status="error"
                                            text="Failed"
                                        />
                                    )

                            ),
                    },

                ],
                []
            );


        // =================================================
        // CONNECTION DISPLAY
        // =================================================

        const connectionLabel =
            wsConnected
                ? "LIVE"
                : connectionStatus ===
                  "connecting"
                    ? "CONNECTING"
                    : "POLLING";


        const connectionColor =
            wsConnected
                ? "green"
                : connectionStatus ===
                  "connecting"
                    ? "orange"
                    : "gold";


        // =================================================
        // NO YEAR
        // =================================================

        if (!selectedYear) {

            return (
                <div
                    style={{
                        padding:
                            24,
                    }}
                >

                    <Alert
                        type="warning"
                        showIcon
                        message="No Active Academic Year"
                        description={
                            "Select an active academic year before opening the Teacher Smart Attendance Monitoring Dashboard."
                        }
                    />

                </div>
            );

        }


        // =================================================
        // RENDER
        // =================================================

        return (

            <div
                className="teacher-smart-monitor"
            >

                <div
                    className="monitor-container"
                >

                    {/* ================================= */}
                    {/* HEADER */}
                    {/* ================================= */}

                    <Card
                        className="monitor-header"
                        bordered={false}
                    >

                        <Row
                            gutter={[
                                20,
                                20,
                            ]}
                            align="middle"
                            justify="space-between"
                        >

                            <Col
                                xs={24}
                                lg={14}
                            >

                                <Space
                                    align="start"
                                    size={16}
                                >

                                    <div
                                        className="monitor-logo"
                                    >

                                        <DashboardOutlined />

                                    </div>


                                    <div>

                                        <Title
                                            level={2}
                                            className="monitor-title"
                                        >
                                            Teacher Smart
                                            Attendance
                                            Monitor
                                        </Title>


                                        <Text
                                            className="monitor-subtitle"
                                        >
                                            Real-time teacher
                                            QR attendance
                                            activity and
                                            scanner monitoring
                                        </Text>


                                        <div
                                            className="header-tags"
                                        >

                                            <Tag
                                                color="orange"
                                            >
                                                Academic Year:
                                                {" "}
                                                {
                                                    selectedYear.name
                                                }
                                            </Tag>


                                            <Tag
                                                icon={
                                                    <SignalFilled />
                                                }
                                                color={
                                                    connectionColor
                                                }
                                            >
                                                {
                                                    connectionLabel
                                                }
                                            </Tag>

                                        </div>

                                    </div>

                                </Space>

                            </Col>


                            <Col
                                xs={24}
                                lg={10}
                            >

                                <div
                                    className="header-right"
                                >

                                    <div>

                                        <Text
                                            type="secondary"
                                        >
                                            Last Updated
                                        </Text>

                                        <div>

                                            <Text
                                                strong
                                            >
                                                {
                                                    lastUpdated
                                                        ? formatDateTime(
                                                            lastUpdated
                                                        )
                                                        : "Loading..."
                                                }
                                            </Text>

                                        </div>

                                    </div>


                                    <div
                                        className="live-indicator"
                                    >

                                        <span
                                            className={
                                                wsConnected
                                                    ? "live-dot"
                                                    : "live-dot offline"
                                            }
                                        />

                                        <Text
                                            strong
                                        >
                                            {
                                                wsConnected
                                                    ? "LIVE MONITORING"
                                                    : "AUTO REFRESH"
                                            }
                                        </Text>

                                    </div>

                                </div>

                            </Col>

                        </Row>

                    </Card>


                    {/* ================================= */}
                    {/* ERROR */}
                    {/* ================================= */}

                    {error && (

                        <Alert
                            type="warning"
                            showIcon
                            closable
                            message={
                                error
                            }
                            description={
                                "The dashboard will continue trying to retrieve attendance information."
                            }
                            style={{
                                marginBottom:
                                    16,
                            }}
                            onClose={() =>
                                setError(
                                    null
                                )
                            }
                        />

                    )}


                    {/* ================================= */}
                    {/* LIVE EVENT */}
                    {/* ================================= */}

                    {liveEvent && (

                        <Card
                            className="live-event-card"
                            bordered={false}
                        >

                            <div
                                className="live-event-content"
                            >

                                <div
                                    className="live-event-icon"
                                >
                                    {
                                        getActionIcon(
                                            liveEvent.action
                                        )
                                    }
                                </div>


                                <div
                                    className="live-event-info"
                                >

                                    <Text
                                        className="live-event-label"
                                    >
                                        NEW SCAN
                                    </Text>


                                    <Text
                                        strong
                                        className="live-event-teacher"
                                    >
                                        {
                                            getTeacherName(
                                                liveEvent
                                            )
                                        }
                                    </Text>


                                    <Text
                                        type="secondary"
                                    >
                                        {
                                            getActionLabel(
                                                liveEvent.action
                                            )
                                        }
                                        {" • "}
                                        {
                                            getGateName(
                                                liveEvent
                                            )
                                        }
                                        {" • "}
                                        {
                                            formatTime(
                                                getTimestamp(
                                                    liveEvent
                                                )
                                            )
                                        }
                                    </Text>

                                </div>


                                <Tag
                                    color={
                                        getActionColor(
                                            liveEvent.action
                                        )
                                    }
                                >
                                    LIVE
                                </Tag>

                            </div>

                        </Card>

                    )}


                    {/* ================================= */}
                    {/* KPI CARDS */}
                    {/* ================================= */}

                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                        className="kpi-row"
                    >

                        {/* Total Scans */}

                        <Col
                            xs={24}
                            sm={12}
                            lg={6}
                        >

                            <Card
                                className="kpi-card kpi-orange"
                                bordered={false}
                            >

                                <Statistic
                                    title="Today's Scans"
                                    value={
                                        statistics.total
                                    }
                                    prefix={
                                        <ScanOutlined />
                                    }
                                />


                                <div
                                    className="kpi-footer"
                                >
                                    All scanner activity
                                </div>

                            </Card>

                        </Col>


                        {/* Check In */}

                        <Col
                            xs={24}
                            sm={12}
                            lg={6}
                        >

                            <Card
                                className="kpi-card"
                                bordered={false}
                            >

                                <Statistic
                                    title="Check-Ins"
                                    value={
                                        statistics.checkIns
                                            .length
                                    }
                                    prefix={
                                        <LoginOutlined />
                                    }
                                    valueStyle={{
                                        color:
                                            COLORS.green,
                                    }}
                                />


                                <div
                                    className="kpi-footer"
                                >
                                    Teachers entering
                                </div>

                            </Card>

                        </Col>


                        {/* Check Out */}

                        <Col
                            xs={24}
                            sm={12}
                            lg={6}
                        >

                            <Card
                                className="kpi-card"
                                bordered={false}
                            >

                                <Statistic
                                    title="Check-Outs"
                                    value={
                                        statistics.checkOuts
                                            .length
                                    }
                                    prefix={
                                        <LogoutOutlined />
                                    }
                                    valueStyle={{
                                        color:
                                            COLORS.blue,
                                    }}
                                />


                                <div
                                    className="kpi-footer"
                                >
                                    Teachers leaving
                                </div>

                            </Card>

                        </Col>


                        {/* Success Rate */}

                        <Col
                            xs={24}
                            sm={12}
                            lg={6}
                        >

                            <Card
                                className="kpi-card"
                                bordered={false}
                            >

                                <Statistic
                                    title="Success Rate"
                                    value={
                                        statistics.successRate
                                    }
                                    suffix="%"
                                    prefix={
                                        <SafetyCertificateFilled />
                                    }
                                    valueStyle={{
                                        color:
                                            statistics.successRate >= 90
                                                ? COLORS.green
                                                : COLORS.orange,
                                    }}
                                />


                                <div
                                    className="kpi-footer"
                                >

                                    {
                                        statistics.uniqueTeachers
                                    }
                                    {" "}
                                    unique teachers

                                </div>

                            </Card>

                        </Col>

                    </Row>


                    {/* ================================= */}
                    {/* CHARTS ROW */}
                    {/* ================================= */}

                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                        className="chart-row"
                    >

                        {/* HOURLY ACTIVITY */}

                        <Col
                            xs={24}
                            xl={16}
                        >

                            <Card
                                bordered={false}
                                className="dashboard-card"
                                title={
                                    <div
                                        className="card-heading"
                                    >

                                        <div
                                            className="card-heading-icon"
                                        >
                                            <ClockCircleFilled />
                                        </div>

                                        <div>

                                            <Text
                                                strong
                                                className="card-title"
                                            >
                                                Today's Scan
                                                Activity
                                            </Text>

                                            <Text
                                                type="secondary"
                                                className="card-description"
                                            >
                                                Hourly check-in,
                                                check-out and
                                                exception activity
                                            </Text>

                                        </div>

                                    </div>
                                }
                            >

                                <div
                                    className="chart-container"
                                >

                                    <ResponsiveContainer
                                        width="100%"
                                        height={330}
                                    >

                                        <BarChart
                                            data={
                                                hourlyData
                                            }
                                            margin={{
                                                top:
                                                    10,

                                                right:
                                                    10,

                                                left:
                                                    -20,

                                                bottom:
                                                    10,
                                            }}
                                        >

                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                vertical={false}
                                            />


                                            <XAxis
                                                dataKey="label"
                                                tick={{
                                                    fontSize:
                                                        11,
                                                }}
                                                interval={
                                                    1
                                                }
                                            />


                                            <YAxis
                                                allowDecimals={
                                                    false
                                                }
                                                tick={{
                                                    fontSize:
                                                        11,
                                                }}
                                            />


                                            <Tooltip />


                                            <Bar
                                                dataKey="checkIn"
                                                name="Check In"
                                                fill={
                                                    COLORS.orange
                                                }
                                                radius={[
                                                    4,
                                                    4,
                                                    0,
                                                    0,
                                                ]}
                                            />


                                            <Bar
                                                dataKey="checkOut"
                                                name="Check Out"
                                                fill={
                                                    COLORS.black
                                                }
                                                radius={[
                                                    4,
                                                    4,
                                                    0,
                                                    0,
                                                ]}
                                            />


                                            <Bar
                                                dataKey="rejected"
                                                name="Rejected"
                                                fill={
                                                    COLORS.red
                                                }
                                                radius={[
                                                    4,
                                                    4,
                                                    0,
                                                    0,
                                                ]}
                                            />

                                        </BarChart>

                                    </ResponsiveContainer>

                                </div>

                            </Card>

                        </Col>


                        {/* ACTION DISTRIBUTION */}

                        <Col
                            xs={24}
                            xl={8}
                        >

                            <Card
                                bordered={false}
                                className="dashboard-card"
                                title={
                                    <div
                                        className="card-heading"
                                    >

                                        <div
                                            className="card-heading-icon"
                                        >
                                            <ScanOutlined />
                                        </div>

                                        <div>

                                            <Text
                                                strong
                                                className="card-title"
                                            >
                                                Scan Distribution
                                            </Text>

                                            <Text
                                                type="secondary"
                                                className="card-description"
                                            >
                                                Today's attendance
                                                actions
                                            </Text>

                                        </div>

                                    </div>
                                }
                            >

                                <div
                                    className="pie-container"
                                >

                                    <ResponsiveContainer
                                        width="100%"
                                        height={270}
                                    >

                                        <PieChart>

                                            <Pie
                                                data={
                                                    actionChartData
                                                }
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={
                                                    65
                                                }
                                                outerRadius={
                                                    100
                                                }
                                                paddingAngle={
                                                    3
                                                }
                                            >

                                                {
                                                    actionChartData.map(
                                                        (
                                                            entry,
                                                            index
                                                        ) => (

                                                            <Cell
                                                                key={
                                                                    `cell-${index}`
                                                                }
                                                                fill={
                                                                    entry.color
                                                                }
                                                            />

                                                        )
                                                    )
                                                }

                                            </Pie>


                                            <Tooltip />

                                        </PieChart>

                                    </ResponsiveContainer>


                                    <div
                                        className="pie-center"
                                    >

                                        <Text
                                            strong
                                            className="pie-total"
                                        >
                                            {
                                                statistics.total
                                            }
                                        </Text>

                                        <Text
                                            type="secondary"
                                        >
                                            Scans
                                        </Text>

                                    </div>

                                </div>


                                <div
                                    className="legend-grid"
                                >

                                    {
                                        actionChartData.map(
                                            item => (

                                                <div
                                                    className="legend-item"
                                                    key={
                                                        item.name
                                                    }
                                                >

                                                    <span
                                                        className="legend-dot"
                                                        style={{
                                                            background:
                                                                item.color,
                                                        }}
                                                    />

                                                    <Text>
                                                        {
                                                            item.name
                                                        }
                                                    </Text>

                                                    <Text
                                                        strong
                                                    >
                                                        {
                                                            item.value
                                                        }
                                                    </Text>

                                                </div>

                                            )
                                        )
                                    }

                                </div>

                            </Card>

                        </Col>

                    </Row>


                    {/* ================================= */}
                    {/* SECOND ANALYTICS ROW */}
                    {/* ================================= */}

                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                    >

                        {/* SUCCESS RATE */}

                        <Col
                            xs={24}
                            md={8}
                        >

                            <Card
                                bordered={false}
                                className="dashboard-card compact-card"
                            >

                                <div
                                    className="section-heading"
                                >

                                    <SafetyCertificateFilled />

                                    <Text
                                        strong
                                    >
                                        Attendance Reliability
                                    </Text>

                                </div>


                                <div
                                    className="success-meter"
                                >

                                    <Progress
                                        type="dashboard"
                                        percent={
                                            statistics.successRate
                                        }
                                        strokeColor={
                                            statistics.successRate >= 90
                                                ? COLORS.green
                                                : COLORS.orange
                                        }
                                        size={150}
                                    />

                                </div>


                                <Text
                                    type="secondary"
                                    className="center-text"
                                >
                                    Successful scanner
                                    transactions today
                                </Text>

                            </Card>

                        </Col>


                        {/* ACTIVE TEACHERS */}

                        <Col
                            xs={24}
                            md={8}
                        >

                            <Card
                                bordered={false}
                                className="dashboard-card compact-card"
                                title={
                                    <div
                                        className="section-heading"
                                    >

                                        <TeamOutlined />

                                        <Text
                                            strong
                                        >
                                            Active Teachers
                                        </Text>

                                    </div>
                                }
                            >

                                <Statistic
                                    value={
                                        activeTeachers.length
                                    }
                                    suffix={
                                        "teachers"
                                    }
                                    valueStyle={{
                                        color:
                                            COLORS.orange,
                                        fontWeight:
                                            700,
                                    }}
                                />


                                <Divider
                                    style={{
                                        margin:
                                            "14px 0",
                                    }}
                                />


                                <div
                                    className="active-teacher-list"
                                >

                                    {
                                        activeTeachers
                                            .slice(
                                                0,
                                                5
                                            )
                                            .map(
                                                teacher => (

                                                    <div
                                                        className="active-teacher-item"
                                                        key={
                                                            teacher.id
                                                        }
                                                    >

                                                        <div
                                                            className="teacher-avatar"
                                                        >
                                                            {
                                                                teacher.name
                                                                    .charAt(
                                                                        0
                                                                    )
                                                                    .toUpperCase()
                                                            }
                                                        </div>


                                                        <div
                                                            className="active-teacher-info"
                                                        >

                                                            <Text
                                                                strong
                                                            >
                                                                {
                                                                    teacher.name
                                                                }
                                                            </Text>

                                                            <Text
                                                                type="secondary"
                                                                style={{
                                                                    fontSize:
                                                                        12,
                                                                }}
                                                            >
                                                                Checked in at
                                                                {" "}
                                                                {
                                                                    formatTime(
                                                                        teacher.lastTime
                                                                    )
                                                                }
                                                            </Text>

                                                        </div>


                                                        <Badge
                                                            status="success"
                                                        />

                                                    </div>

                                                )
                                            )
                                    }


                                    {
                                        activeTeachers.length ===
                                            0 && (

                                            <Empty
                                                image={
                                                    Empty.PRESENTED_IMAGE_SIMPLE
                                                }
                                                description="No active teachers detected"
                                            />

                                        )
                                    }

                                </div>

                            </Card>

                        </Col>


                        {/* DEVICE MONITORING */}

                        <Col
                            xs={24}
                            md={8}
                        >

                            <Card
                                bordered={false}
                                className="dashboard-card compact-card"
                                title={
                                    <div
                                        className="section-heading"
                                    >

                                        <WifiOutlined />

                                        <Text
                                            strong
                                        >
                                            Scanner Devices
                                        </Text>

                                    </div>
                                }
                            >

                                {
                                    deviceData.length ===
                                        0 ? (

                                        <Empty
                                            image={
                                                Empty.PRESENTED_IMAGE_SIMPLE
                                            }
                                            description="No scanner activity"
                                        />

                                    ) : (

                                        <div
                                            className="device-list"
                                        >

                                            {
                                                deviceData
                                                    .slice(
                                                        0,
                                                        5
                                                    )
                                                    .map(
                                                        device => (

                                                            <div
                                                                className="device-item"
                                                                key={
                                                                    device.device
                                                                }
                                                            >

                                                                <div
                                                                    className="device-icon"
                                                                >
                                                                    <ScanOutlined />
                                                                </div>


                                                                <div
                                                                    className="device-info"
                                                                >

                                                                    <Text
                                                                        strong
                                                                    >
                                                                        {
                                                                            device.device
                                                                        }
                                                                    </Text>

                                                                    <Text
                                                                        type="secondary"
                                                                    >
                                                                        {
                                                                            device.gate
                                                                        }
                                                                    </Text>

                                                                </div>


                                                                <div
                                                                    className="device-count"
                                                                >

                                                                    <Text
                                                                        strong
                                                                    >
                                                                        {
                                                                            device.scans
                                                                        }
                                                                    </Text>

                                                                    <Text
                                                                        type="secondary"
                                                                    >
                                                                        scans
                                                                    </Text>

                                                                </div>

                                                            </div>

                                                        )
                                                    )
                                            }

                                        </div>

                                    )
                                }

                            </Card>

                        </Col>

                    </Row>


                    {/* ================================= */}
                    {/* LIVE ATTENDANCE TABLE */}
                    {/* ================================= */}

                    <Card
                        bordered={false}
                        className="dashboard-card live-table-card"
                        title={
                            <div
                                className="table-header"
                            >

                                <div
                                    className="card-heading"
                                >

                                    <div
                                        className="card-heading-icon"
                                    >
                                        <SignalFilled />
                                    </div>

                                    <div>

                                        <Text
                                            strong
                                            className="card-title"
                                        >
                                            Live Attendance
                                            Activity
                                        </Text>

                                        <Text
                                            type="secondary"
                                            className="card-description"
                                        >
                                            New scans appear
                                            automatically
                                        </Text>

                                    </div>

                                </div>


                                <Space
                                    wrap
                                >

                                    <Badge
                                        status={
                                            wsConnected
                                                ? "success"
                                                : "warning"
                                        }
                                        text={
                                            wsConnected
                                                ? "Real-time"
                                                : "Polling"
                                        }
                                    />


                                    <button
                                        className="refresh-button"
                                        onClick={() =>
                                            fetchLogs(
                                                false
                                            )
                                        }
                                        disabled={
                                            refreshing
                                        }
                                    >

                                        <ReloadOutlined
                                            spin={
                                                refreshing
                                            }
                                        />

                                        Refresh

                                    </button>

                                </Space>

                            </div>
                        }
                    >

                        <Table
                            rowKey={
                                record =>
                                    record.id ||
                                    `${getTimestamp(
                                        record
                                    )}-${getTeacherName(
                                        record
                                    )}-${Math.random()}`
                            }
                            columns={
                                columns
                            }
                            dataSource={
                                logs
                            }
                            loading={
                                loading
                            }
                            pagination={{
                                pageSize:
                                    15,

                                showSizeChanger:
                                    true,

                                pageSizeOptions:
                                    [
                                        "10",
                                        "15",
                                        "25",
                                        "50",
                                    ],

                                showTotal:
                                    total =>
                                        `${total} scan records`,
                            }}
                            scroll={{
                                x:
                                    850,
                            }}
                            locale={{
                                emptyText:
                                    "No attendance scans recorded yet.",
                            }}
                        />

                    </Card>


                    {/* ================================= */}
                    {/* FOOTER */}
                    {/* ================================= */}

                    <div
                        className="monitor-footer"
                    >

                        <Space
                            wrap
                            justify="center"
                        >

                            <Text
                                type="secondary"
                            >
                                <SafetyCertificateFilled />
                                {" "}
                                Secure Teacher
                                Attendance Monitoring
                            </Text>


                            <Text
                                type="secondary"
                            >
                                •
                            </Text>


                            <Text
                                type="secondary"
                            >
                                Academic Year:
                                {" "}
                                {
                                    selectedYear.name
                                }
                            </Text>


                            <Text
                                type="secondary"
                            >
                                •
                            </Text>


                            <Text
                                type="secondary"
                            >
                                {
                                    wsConnected
                                        ? "WebSocket Connected"
                                        : "Automatic Polling"
                                }
                            </Text>

                        </Space>

                    </div>

                </div>


                {/* ========================================= */}
                {/* DASHBOARD CSS */}
                {/* ========================================= */}

                <style>
                    {`

                    * {
                        box-sizing:
                            border-box;
                    }


                    .teacher-smart-monitor {
                        min-height:
                            100vh;

                        background:
                            #f3f4f6;

                        padding:
                            18px;

                        color:
                            #111827;
                    }


                    .monitor-container {
                        max-width:
                            1500px;

                        margin:
                            0 auto;
                    }


                    /* =====================================
                       HEADER
                    ===================================== */

                    .monitor-header {
                        border-radius:
                            18px !important;

                        margin-bottom:
                            16px;

                        overflow:
                            hidden;

                        position:
                            relative;
                    }


                    .monitor-header::before {
                        content:
                            "";

                        position:
                            absolute;

                        left:
                            0;

                        top:
                            0;

                        bottom:
                            0;

                        width:
                            6px;

                        background:
                            #f97316;
                    }


                    .monitor-logo {
                        width:
                            62px;

                        height:
                            62px;

                        min-width:
                            62px;

                        border-radius:
                            16px;

                        background:
                            #111827;

                        color:
                            #f97316;

                        display:
                            flex;

                        align-items:
                            center;

                        justify-content:
                            center;

                        font-size:
                            30px;

                        box-shadow:
                            0 8px 20px
                            rgba(
                                17,
                                24,
                                39,
                                0.15
                            );
                    }


                    .monitor-title {
                        margin:
                            0 !important;

                        color:
                            #111827 !important;

                        font-weight:
                            800 !important;

                        letter-spacing:
                            -0.5px;
                    }


                    .monitor-subtitle {
                        display:
                            block;

                        margin-top:
                            4px;

                        font-size:
                            14px;
                    }


                    .header-tags {
                        display:
                            flex;

                        flex-wrap:
                            wrap;

                        gap:
                            8px;

                        margin-top:
                            12px;
                    }


                    .header-right {
                        display:
                            flex;

                        justify-content:
                            flex-end;

                        align-items:
                            center;

                        gap:
                            24px;

                        text-align:
                            right;
                    }


                    .live-indicator {
                        display:
                            flex;

                        align-items:
                            center;

                        gap:
                            8px;

                        white-space:
                            nowrap;
                    }


                    .live-dot {
                        width:
                            10px;

                        height:
                            10px;

                        border-radius:
                            50%;

                        background:
                            #16a34a;

                        box-shadow:
                            0 0 0 5px
                            rgba(
                                22,
                                163,
                                74,
                                0.12
                            );

                        animation:
                            monitorPulse
                            1.6s
                            infinite;
                    }


                    .live-dot.offline {
                        background:
                            #f97316;

                        box-shadow:
                            0 0 0 5px
                            rgba(
                                249,
                                115,
                                22,
                                0.12
                            );
                    }


                    @keyframes monitorPulse {

                        0% {
                            opacity:
                                1;

                            transform:
                                scale(1);
                        }

                        50% {
                            opacity:
                                0.45;

                            transform:
                                scale(0.8);
                        }

                        100% {
                            opacity:
                                1;

                            transform:
                                scale(1);
                        }

                    }


                    /* =====================================
                       LIVE EVENT
                    ===================================== */

                    .live-event-card {
                        border-radius:
                            16px !important;

                        margin-bottom:
                            16px;

                        border-left:
                            5px solid
                            #f97316 !important;

                        background:
                            linear-gradient(
                                135deg,
                                #fff7ed,
                                #ffffff
                            ) !important;

                        animation:
                            liveEventAppear
                            0.3s
                            ease-out;
                    }


                    @keyframes liveEventAppear {

                        from {
                            opacity:
                                0;

                            transform:
                                translateY(
                                    -8px
                                );
                        }

                        to {
                            opacity:
                                1;

                            transform:
                                translateY(
                                    0
                                );
                        }

                    }


                    .live-event-content {
                        display:
                            flex;

                        align-items:
                            center;

                        gap:
                            14px;
                    }


                    .live-event-icon {
                        width:
                            48px;

                        height:
                            48px;

                        min-width:
                            48px;

                        border-radius:
                            12px;

                        background:
                            #111827;

                        color:
                            #f97316;

                        display:
                            flex;

                        align-items:
                            center;

                        justify-content:
                            center;

                        font-size:
                            22px;
                    }


                    .live-event-info {
                        flex:
                            1;

                        display:
                            flex;

                        flex-direction:
                            column;

                        gap:
                            2px;
                    }


                    .live-event-label {
                        color:
                            #f97316;

                        font-size:
                            11px;

                        font-weight:
                            800;

                        letter-spacing:
                            1px;
                    }


                    .live-event-teacher {
                        font-size:
                            17px;
                    }


                    /* =====================================
                       KPI
                    ===================================== */

                    .kpi-row {
                        margin-bottom:
                            16px;
                    }


                    .kpi-card {
                        border-radius:
                            16px !important;

                        height:
                            100%;

                        overflow:
                            hidden;

                        position:
                            relative;

                        transition:
                            transform
                            0.2s
                            ease,
                            box-shadow
                            0.2s
                            ease;
                    }


                    .kpi-card:hover {
                        transform:
                            translateY(
                                -3px
                            );

                        box-shadow:
                            0 12px 30px
                            rgba(
                                17,
                                24,
                                39,
                                0.10
                            );
                    }


                    .kpi-card::before {
                        content:
                            "";

                        position:
                            absolute;

                        left:
                            0;

                        top:
                            0;

                        bottom:
                            0;

                        width:
                            4px;

                        background:
                            #111827;
                    }


                    .kpi-orange::before {
                        background:
                            #f97316;
                    }


                    .kpi-card .ant-statistic-title {
                        font-weight:
                            600;

                        color:
                            #6b7280;

                        font-size:
                            13px;
                    }


                    .kpi-card .ant-statistic-content {
                        font-weight:
                            800;

                        color:
                            #111827;
                    }


                    .kpi-card .ant-statistic-content-prefix {
                        margin-right:
                            8px;
                    }


                    .kpi-footer {
                        margin-top:
                            8px;

                        color:
                            #6b7280;

                        font-size:
                            12px;
                    }


                    /* =====================================
                       CARDS
                    ===================================== */

                    .dashboard-card {
                        border-radius:
                            16px !important;

                        height:
                            100%;

                        box-shadow:
                            0 2px 8px
                            rgba(
                                0,
                                0,
                                0,
                                0.045
                            );
                    }


                    .compact-card {
                        min-height:
                            250px;
                    }


                    .card-heading {
                        display:
                            flex;

                        align-items:
                            center;

                        gap:
                            12px;
                    }


                    .card-heading-icon {
                        width:
                            38px;

                        height:
                            38px;

                        border-radius:
                            10px;

                        display:
                            flex;

                        align-items:
                            center;

                        justify-content:
                            center;

                        background:
                            #fff7ed;

                        color:
                            #f97316;
                    }


                    .card-title {
                        display:
                            block;

                        font-size:
                            16px;

                        color:
                            #111827;
                    }


                    .card-description {
                        display:
                            block;

                        font-size:
                            12px;

                        margin-top:
                            2px;
                    }


                    .chart-container {
                        width:
                            100%;

                        min-height:
                            330px;
                    }


                    .pie-container {
                        position:
                            relative;
                    }


                    .pie-center {
                        position:
                            absolute;

                        left:
                            50%;

                        top:
                            50%;

                        transform:
                            translate(
                                -50%,
                                -50%
                            );

                        display:
                            flex;

                        flex-direction:
                            column;

                        align-items:
                            center;

                        pointer-events:
                            none;
                    }


                    .pie-total {
                        font-size:
                            28px;

                        line-height:
                            1;

                        color:
                            #111827;
                    }


                    .legend-grid {
                        display:
                            grid;

                        grid-template-columns:
                            repeat(
                                2,
                                minmax(
                                    0,
                                    1fr
                                )
                            );

                        gap:
                            8px;
                    }


                    .legend-item {
                        display:
                            flex;

                        align-items:
                            center;

                        gap:
                            7px;

                        padding:
                            7px;

                        background:
                            #f9fafb;

                        border-radius:
                            8px;
                    }


                    .legend-item strong {
                        margin-left:
                            auto;
                    }


                    .legend-dot {
                        width:
                            8px;

                        height:
                            8px;

                        border-radius:
                            50%;
                    }


                    /* =====================================
                       SECTION
                    ===================================== */

                    .section-heading {
                        display:
                            flex;

                        align-items:
                            center;

                        gap:
                            8px;

                        color:
                            #111827;

                        margin-bottom:
                            12px;
                    }


                    .section-heading > span {
                        color:
                            #f97316;
                    }


                    .success-meter {
                        display:
                            flex;

                        justify-content:
                            center;

                        padding:
                            8px 0;
                    }


                    .center-text {
                        display:
                            block;

                        text-align:
                            center;
                    }


                    /* =====================================
                       ACTIVE TEACHERS
                    ===================================== */

                    .active-teacher-list {
                        display:
                            flex;

                        flex-direction:
                            column;

                        gap:
                            9px;
                    }


                    .active-teacher-item {
                        display:
                            flex;

                        align-items:
                            center;

                        gap:
                            9px;

                        padding:
                            7px;

                        border-radius:
                            9px;

                        background:
                            #f9fafb;
                    }


                    .teacher-avatar {
                        width:
                            36px;

                        height:
                            36px;

                        min-width:
                            36px;

                        border-radius:
                            10px;

                        background:
                            #111827;

                        color:
                            #f97316;

                        display:
                            flex;

                        align-items:
                            center;

                        justify-content:
                            center;

                        font-weight:
                            800;

                        font-size:
                            14px;
                    }


                    .active-teacher-info {
                        flex:
                            1;

                        min-width:
                            0;

                        display:
                            flex;

                        flex-direction:
                            column;
                    }


                    /* =====================================
                       DEVICES
                    ===================================== */

                    .device-list {
                        display:
                            flex;

                        flex-direction:
                            column;

                        gap:
                            10px;
                    }


                    .device-item {
                        display:
                            flex;

                        align-items:
                            center;

                        gap:
                            9px;

                        padding:
                            8px;

                        background:
                            #f9fafb;

                        border-radius:
                            10px;
                    }


                    .device-icon {
                        width:
                            36px;

                        height:
                            36px;

                        border-radius:
                            9px;

                        display:
                            flex;

                        align-items:
                            center;

                        justify-content:
                            center;

                        background:
                            #111827;

                        color:
                            #f97316;
                    }


                    .device-info {
                        flex:
                            1;

                        min-width:
                            0;

                        display:
                            flex;

                        flex-direction:
                            column;
                    }


                    .device-count {
                        display:
                            flex;

                        flex-direction:
                            column;

                        align-items:
                            flex-end;
                    }


                    /* =====================================
                       TABLE
                    ===================================== */

                    .live-table-card {
                        margin-top:
                            16px;
                    }


                    .table-header {
                        display:
                            flex;

                        align-items:
                            center;

                        justify-content:
                            space-between;

                        gap:
                            15px;

                        width:
                            100%;
                    }


                    .refresh-button {
                        border:
                            1px solid
                            #e5e7eb;

                        background:
                            #ffffff;

                        color:
                            #111827;

                        border-radius:
                            8px;

                        padding:
                            7px 12px;

                        cursor:
                            pointer;

                        display:
                            inline-flex;

                        align-items:
                            center;

                        gap:
                            7px;

                        font-weight:
                            600;
                    }


                    .refresh-button:hover {
                        border-color:
                            #f97316;

                        color:
                            #f97316;
                    }


                    .refresh-button:disabled {
                        opacity:
                            0.6;

                        cursor:
                            not-allowed;
                    }


                    .live-table-card
                        .ant-table-thead
                        > tr
                        > th {
                        background:
                            #111827 !important;

                        color:
                            #ffffff !important;

                        font-size:
                            11px;

                        font-weight:
                            700;

                        letter-spacing:
                            0.5px;
                    }


                    .live-table-card
                        .ant-table-tbody
                        > tr:hover
                        > td {
                        background:
                            #fff7ed !important;
                    }


                    .live-table-card
                        .ant-table-tbody
                        > tr
                        > td {
                        vertical-align:
                            middle;
                    }


                    /* =====================================
                       FOOTER
                    ===================================== */

                    .monitor-footer {
                        text-align:
                            center;

                        padding:
                            22px 10px;
                    }


                    /* =====================================
                       MOBILE
                    ===================================== */

                    @media (
                        max-width:
                            767px
                    ) {

                        .teacher-smart-monitor {
                            padding:
                                8px;
                        }


                        .monitor-header {
                            border-radius:
                                14px !important;
                        }


                        .monitor-logo {
                            width:
                                50px;

                            height:
                                50px;

                            min-width:
                                50px;

                            font-size:
                                23px;
                        }


                        .monitor-title {
                            font-size:
                                22px !important;
                        }


                        .monitor-subtitle {
                            font-size:
                                12px;
                        }


                        .header-right {
                            justify-content:
                                space-between;

                            text-align:
                                left;

                            width:
                                100%;
                        }


                        .live-event-content {
                            align-items:
                                flex-start;
                        }


                        .live-event-icon {
                            width:
                                40px;

                            height:
                                40px;

                            min-width:
                                40px;

                            font-size:
                                18px;
                        }


                        .live-event-teacher {
                            font-size:
                                15px;
                        }


                        .dashboard-card {
                            border-radius:
                                13px !important;
                        }


                        .card-title {
                            font-size:
                                14px;
                        }


                        .card-description {
                            font-size:
                                11px;
                        }


                        .chart-container {
                            min-height:
                                280px;
                        }


                        .table-header {
                            align-items:
                                flex-start;

                            flex-direction:
                                column;
                        }


                        .refresh-button {
                            width:
                                100%;

                            justify-content:
                                center;
                        }


                        .live-table-card
                            .ant-table {
                            font-size:
                                12px;
                        }


                        .live-table-card
                            .ant-table-thead
                            > tr
                            > th {
                            padding:
                                9px 8px !important;
                        }


                        .live-table-card
                            .ant-table-tbody
                            > tr
                            > td {
                            padding:
                                9px 8px !important;
                        }

                    }


                    @media (
                        max-width:
                            480px
                    ) {

                        .monitor-header
                            .ant-card-body {
                            padding:
                                14px !important;
                        }


                        .monitor-title {
                            font-size:
                                19px !important;
                        }


                        .monitor-logo {
                            display:
                                none;
                        }


                        .header-right {
                            flex-direction:
                                column;

                            align-items:
                                flex-start;

                            gap:
                                10px;
                        }


                        .header-tags {
                            gap:
                                5px;
                        }


                        .kpi-card
                            .ant-statistic-content {
                            font-size:
                                25px;
                        }


                        .legend-grid {
                            grid-template-columns:
                                1fr;
                        }

                    }

                    `}
                </style>

            </div>

        );

    };


export default TeacherSmartAttendanceDashboard;